/**
 * ASIN: формат и разбор того, что пользователь ввёл или вставил в поле.
 *
 * Формат строже бэкендового (`backend/schemas/calculator.py: ASIN_PATTERN` — «10 латинских
 * букв и цифр»): товарный ASIN начинается с `B0`, книжный совпадает с ISBN-10. По целому
 * ASIN виджеты калькулятора сами идут в платный Keepa, и при «10 любых символах» туда
 * уходили `HELLOWORLD` и `AAAAAAAAAA` (QA 09.10, WS 15587410).
 */
const PRODUCT_ASIN_RE = /^B0[A-Z0-9]{8}$/;
const ISBN10_RE = /^\d{9}[\dX]$/;
const ISBN10_MODULUS = 11;
const ASIN_LENGTH = 10;

// ASIN в адресе товара: /dp/<ASIN>, /gp/product/<ASIN>, /gp/aw/d/<ASIN>.
const PRODUCT_PATH_ASIN_RE = /\/(?:dp|gp\/product|gp\/aw\/d)\/([A-Za-z0-9]{10})(?=[/?#]|$)/;
// Похоже на ссылку, а не на ASIN: со схемой, с www. или с доменом Amazon / его сокращателя.
const LINK_LIKE_RE = /^(?:https?:\/\/|www\.|(?:smile\.)?amazon\.|amzn\.|a\.co\/)/i;
// Сокращатели Amazon: адрес товара за ними получить можно только переходом, из браузера
// модуля — нельзя (редирект на чужой домен).
const SHORT_LINK_HOST_RE = /^(?:amzn\.(?:to|eu|asia)|a\.co)$/;
const AMAZON_HOST_RE = /^amazon\.[a-z.]+$/;
// Калькулятор считает Amazon US: комиссии, Keepa-домен и прогноз — американские.
const US_MARKETPLACE_HOST = 'amazon.com';

/** Контрольная цифра ISBN-10: Σ (10 − i) · dᵢ ≡ 0 (mod 11), X в конце — это 10. */
function isbn10ChecksumOk(value: string): boolean {
  let sum = 0;
  for (let i = 0; i < ASIN_LENGTH; i += 1) {
    const digit = value[i] === 'X' ? 10 : Number(value[i]);
    sum += (ASIN_LENGTH - i) * digit;
  }
  return sum % ISBN10_MODULUS === 0;
}

/**
 * Книжный ASIN проверяется и по контрольной цифре: «1234567890» по форме похож на
 * ISBN-10, но им не является, и уходил платным запросом в Keepa (QA 09.10, WS 15587410).
 */
export function isAsin(value: string): boolean {
  if (PRODUCT_ASIN_RE.test(value)) return true;
  return ISBN10_RE.test(value) && isbn10ChecksumOk(value);
}

/** Почему вставленная ссылка не дала ASIN. */
export type AsinLinkProblem = 'short_link' | 'foreign_marketplace' | 'no_asin_in_link';

export type AsinInput = { asin: string; problem: null } | { asin: null; problem: AsinLinkProblem };

function hostOf(link: string): string | null {
  try {
    const url = new URL(/^https?:\/\//i.test(link) ? link : `https://${link}`);
    return url.hostname.toLowerCase().replace(/^(?:www|smile)\./, '');
  } catch {
    return null;
  }
}

/**
 * Вставка заменяет поле целиком только тогда, когда вставлен целый ASIN или ссылка: так
 * новый ASIN не дописывается к прежнему. Фрагмент вставляется обычным образом — иначе
 * исправить в ASIN пару символов вставкой было нельзя (регрессия, QA 09.10).
 */
export function pasteReplacesField(pasted: string): boolean {
  const text = pasted.trim();
  if (LINK_LIKE_RE.test(text)) return true;
  const cleaned = text.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return cleaned.length >= ASIN_LENGTH && isAsin(cleaned.slice(0, ASIN_LENGTH));
}

/**
 * Ввод поля ASIN → что поставить в поле.
 *
 * Ссылка разбирается целиком: ASIN товара Amazon US — из пути; короткая ссылка, другой
 * маркетплейс или ссылка без ASIN — явный отказ, поле не меняется. Всё прочее — набор с
 * клавиатуры: латиница и цифры в верхнем регистре, длина режется ПОСЛЕ очистки (иначе
 * « B0GVTBQ98P» с пробелом впереди терял последний символ).
 */
export function interpretAsinInput(raw: string): AsinInput {
  const text = raw.trim();
  if (LINK_LIKE_RE.test(text)) {
    const host = hostOf(text);
    if (host === null) return { asin: null, problem: 'no_asin_in_link' };
    if (SHORT_LINK_HOST_RE.test(host)) return { asin: null, problem: 'short_link' };
    if (host !== US_MARKETPLACE_HOST) {
      return { asin: null, problem: AMAZON_HOST_RE.test(host) ? 'foreign_marketplace' : 'no_asin_in_link' };
    }
    const fromPath = PRODUCT_PATH_ASIN_RE.exec(text);
    if (!fromPath) return { asin: null, problem: 'no_asin_in_link' };
    return { asin: fromPath[1].toUpperCase(), problem: null };
  }
  return { asin: text.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, ASIN_LENGTH), problem: null };
}
